import * as React from 'react';
import { degreesToRadians, roundToOne, roundToTwo } from '../../../../../MACRA_1/components/InputForm/Geometry/utils';
import groundImage from '../../../../../images/ground_pattern.svg';
import { getImgSrcWithColor, loadImage } from '../../../../../utils/graphicEngine';
import { ISlopedWeirCrossProps } from './ISlopedWeir';
import textureProtectionSystem from '../../../../../images/texture_protection_system.svg';
import { useAppDispatch } from '../../../../../../reduxService/hooks';
import { setCrossSectionImg } from '../../../../store/project/resultsSlice';
import { EConversionSIToImperial, convertTo } from '../../../../../conversion';

//setup
const setup = {
    gabionColor: 'grey',
    waterColor: '#83d7ee',

    indicatorHeight: 30,
    indicatorWidth: 40,
    font: '11px Arial',
    colorIndicator: 'red',
    indicatorLineWidth: 1,

    borderWidth: 1,
    height: 370,
    width: 540,
    heightSoil: 2,
};

const SlopedWeirCross = (props: ISlopedWeirCrossProps) => {
    const slopedWeirCanvas = React.useRef<HTMLCanvasElement>(null);
    let ctx: CanvasRenderingContext2D;

    let soilImageSrc: HTMLImageElement;
    let baseImageSrc: HTMLImageElement | undefined = undefined;

    const { riverGeometry, wingsGeometry, weirGeometry, result, scale } = props;

    const dispatch = useAppDispatch();

    let angleLeftRad = degreesToRadians(riverGeometry!.slopeOfLeftBank);
    let hypLeft = riverGeometry!.bankTopElevation / Math.sin(angleLeftRad);
    let cxLeft = hypLeft * Math.cos(angleLeftRad);

    let angleRightRad = degreesToRadians(riverGeometry!.slopeOfRightBank);
    let hypRight = riverGeometry!.bankTopElevation / Math.sin(angleRightRad);
    let cxRight = hypRight * Math.cos(angleRightRad);

    const _height = riverGeometry!.bankTopElevation + weirGeometry.liningThickness! + setup.heightSoil;
    const _width = setup.borderWidth + cxLeft + riverGeometry.channelWidth + cxRight + setup.borderWidth;

    const scaleWidth = (setup.width + scale) / _width;
    const scaleHeight = (setup.height + scale) / _height;

    const _scale = Math.ceil(Math.min(scaleWidth, scaleHeight));

    React.useEffect(() => {
        let slopedWeirCanvasEleL = slopedWeirCanvas.current!;
        ctx = slopedWeirCanvasEleL.getContext('2d')!;
        Promise.all([loadSoilImage(), loadBaseImage()]).then(() => {
            cleanCanvas();
            if (!!result) {
                createWaterSection();
            }
            createRiverGeometry();
            createIndicator();
            dispatch(setCrossSectionImg(slopedWeirCanvasEleL.toDataURL('image/png')));
        });
    }, [props]);

    const createWaterSection = () => {
        let z0 = result!.hydraulic.upstreamSection;
        ctx.beginPath();
        ctx.fillStyle = setup.waterColor;
        ctx.moveTo(setup.indicatorWidth, _scale * (riverGeometry!.bankTopElevation - weirGeometry.height! - z0));
        ctx.lineTo(setup.indicatorWidth + _scale * _width, _scale * (riverGeometry!.bankTopElevation - weirGeometry.height! - z0));
        ctx.lineTo(setup.indicatorWidth + _scale * _width, _scale * (riverGeometry!.bankTopElevation - weirGeometry.height!));
        ctx.lineTo(setup.indicatorWidth, _scale * (riverGeometry!.bankTopElevation - weirGeometry.height!));
        ctx.fill();
        ctx.closePath();
    };

    const createIndicator = () => {
        //river geometry bank top elevation
        ctx.save();
        createIndicatorY(0, _scale * riverGeometry.bankTopElevation, setup.indicatorWidth / 2, riverGeometry.bankTopElevation);
        //weir geometry height
        createIndicatorY(
            _scale * (riverGeometry!.bankTopElevation - weirGeometry.height!),
            _scale * riverGeometry.bankTopElevation,
            setup.indicatorWidth + _scale * _width + setup.indicatorWidth / 2,
            weirGeometry.height!,
        );
        //river geometry channel width
        createIndicatorX(
            setup.indicatorWidth + _scale * (setup.borderWidth + cxLeft),
            setup.indicatorWidth + _scale * (setup.borderWidth + cxLeft + riverGeometry.channelWidth),
            _scale * _height + setup.indicatorHeight / 2,
            riverGeometry.channelWidth,
        );
        ctx.restore();
    };

    const createIndicatorY = (moveToY: number, lineToY: number, x: number, value: number) => {
        createLine([], x - 2, moveToY, x + 2, moveToY);
        createLine([], x, moveToY, x, lineToY, roundToOne(props.isImperial ? convertTo(value, EConversionSIToImperial.m_to_ft) : value));
        createLine([], x - 2, lineToY, x + 2, lineToY);
    };

    const createIndicatorX = (moveToX: number, lineToX: number, y: number, value: number) => {
        createLine([], moveToX, y - 2, moveToX, y + 2);
        createLine([], moveToX, y, lineToX, y, roundToOne(props.isImperial ? convertTo(value, EConversionSIToImperial.m_to_ft) : value));
        createLine([], lineToX, y - 2, lineToX, y + 2);
    };

    const createLine = (segments: number[], moveToX: number, moveToY: number, lineToX: number, lineToY: number, value?: number) => {
        ctx.beginPath();
        ctx.setLineDash(segments);
        ctx.moveTo(moveToX, moveToY);
        ctx.lineTo(lineToX, lineToY);
        ctx.strokeStyle = setup.colorIndicator!;
        ctx.font = setup.font;
        ctx.lineWidth = setup.indicatorLineWidth;
        ctx.lineCap = 'round';
        if (value && value > 0) {
            ctx.fillStyle = 'black';
            ctx.fillText(value.toString(), (moveToX + lineToX) / 2 + 2, (moveToY + lineToY) / 2 - 2);
        }
        ctx.stroke();
    };

    const loadSoilImage = async (): Promise<void> => {
        soilImageSrc = await loadImage(groundImage);
    };

    const loadBaseImage = async (): Promise<void> => {
        let imgSrcWithColor = getImgSrcWithColor(textureProtectionSystem, setup.gabionColor);
        baseImageSrc = await loadImage(imgSrcWithColor);
    };

    const cleanCanvas = () => {
        let canvasEle = slopedWeirCanvas.current!;
        ctx = canvasEle.getContext('2d')!;
        ctx.clearRect(0, 0, canvasEle.width, canvasEle.height);
    };

    const createRiverGeometry = () => {
        let basePat = ctx.createPattern(baseImageSrc!, 'repeat');

        //wings
        ctx.beginPath();
        ctx.fillStyle = basePat!;
        ctx.fillRect(
            setup.indicatorWidth,
            _scale * (riverGeometry!.bankTopElevation - weirGeometry.height! - wingsGeometry.height!),
            _scale * (setup.borderWidth + cxLeft + (riverGeometry.channelWidth - wingsGeometry.width) / 2 + wingsGeometry.offset!),
            _scale * (weirGeometry.height! + wingsGeometry.height!),
        );
        ctx.fillRect(
            setup.indicatorWidth + _scale * (setup.borderWidth + cxLeft + (riverGeometry.channelWidth - wingsGeometry.width) / 2 + wingsGeometry.offset!),
            _scale * (riverGeometry!.bankTopElevation - weirGeometry.height!),
            _scale * wingsGeometry.width,
            _scale * weirGeometry.height!,
        );
        ctx.fillRect(
            setup.indicatorWidth + _scale * (setup.borderWidth + cxLeft + (riverGeometry.channelWidth - wingsGeometry.width) / 2 + wingsGeometry.width + wingsGeometry.offset!),
            _scale * (riverGeometry!.bankTopElevation - weirGeometry.height! - wingsGeometry.height!),
            _scale * (setup.borderWidth + cxRight + (riverGeometry.channelWidth - wingsGeometry.width) / 2 - wingsGeometry.offset!),
            _scale * (weirGeometry.height! + wingsGeometry.height!),
        );
        ctx.closePath();

        //river geometry
        let pat = ctx.createPattern(soilImageSrc, 'repeat')!;
        ctx.beginPath();
        ctx.fillStyle = pat;
        ctx.moveTo(setup.indicatorWidth, 0);
        ctx.lineTo(setup.indicatorWidth + _scale * +setup.borderWidth, 0);
        ctx.lineTo(setup.indicatorWidth + _scale * (setup.borderWidth + cxLeft), _scale * riverGeometry!.bankTopElevation);
        ctx.lineTo(setup.indicatorWidth + _scale * (setup.borderWidth + cxLeft + riverGeometry!.channelWidth), _scale * riverGeometry!.bankTopElevation);
        ctx.lineTo(setup.indicatorWidth + _scale * (setup.borderWidth + cxLeft + riverGeometry!.channelWidth + cxRight), 0);
        ctx.lineTo(setup.indicatorWidth + _scale * (setup.borderWidth + cxLeft + riverGeometry!.channelWidth + cxRight + setup.borderWidth), 0);
        ctx.lineTo(setup.indicatorWidth + _scale * _width, _scale * _height);
        ctx.lineTo(setup.indicatorWidth, _scale * _height);
        ctx.lineTo(setup.indicatorWidth, 0);
        ctx.fill();
        ctx.closePath();
        ctx.beginPath();
        ctx.moveTo(setup.indicatorWidth, 0);
        ctx.lineTo(setup.indicatorWidth + _scale * setup.borderWidth, 0);
        ctx.lineTo(setup.indicatorWidth + _scale * (setup.borderWidth + cxLeft), _scale * riverGeometry!.bankTopElevation);
        ctx.lineTo(setup.indicatorWidth + _scale * (setup.borderWidth + cxLeft + riverGeometry!.channelWidth), _scale * riverGeometry!.bankTopElevation);
        ctx.lineTo(setup.indicatorWidth + _scale * (setup.borderWidth + cxLeft + riverGeometry!.channelWidth + cxRight), 0);
        ctx.lineTo(setup.indicatorWidth + _scale * (setup.borderWidth + cxLeft + riverGeometry!.channelWidth + cxRight + setup.borderWidth), 0);
        ctx.stroke();
        ctx.closePath();

        //wings underSoil
        ctx.beginPath();
        ctx.fillStyle = basePat!;
        ctx.fillRect(
            setup.indicatorWidth + _scale * (setup.borderWidth + cxLeft),
            _scale * riverGeometry!.bankTopElevation,
            _scale * ((riverGeometry.channelWidth - wingsGeometry.width) / 2 + wingsGeometry.offset!),
            _scale * weirGeometry.liningThickness!,
        );
        ctx.fillRect(
            setup.indicatorWidth + _scale * (setup.borderWidth + cxLeft + (riverGeometry.channelWidth - wingsGeometry.width) / 2 + wingsGeometry.offset!),
            _scale * riverGeometry!.bankTopElevation,
            _scale * wingsGeometry.width,
            _scale * weirGeometry.liningThickness!,
        );
        ctx.fillRect(
            setup.indicatorWidth + _scale * (setup.borderWidth + cxLeft + (riverGeometry.channelWidth - wingsGeometry.width) / 2 + wingsGeometry.width + wingsGeometry.offset!),
            _scale * riverGeometry!.bankTopElevation,
            _scale * ((riverGeometry.channelWidth - wingsGeometry.width) / 2 - wingsGeometry.offset!),
            _scale * weirGeometry.liningThickness!,
        );
        ctx.closePath();
    };

    return <canvas width={_scale * _width + 2 * setup.indicatorWidth} height={_scale * _height + setup.indicatorHeight} ref={slopedWeirCanvas}></canvas>;
};

export default SlopedWeirCross;
